import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom'; // Import useNavigate, useSearchParams, useParams, useLocation
import apiService from '../services/api';
import CopyableId from '../components/common/CopyableId';
import { useAppContext } from '../context/AppContext'; // Import useAppContext

// Define tab constants
const TABS = {
  MEDIOGRAPHY: 'mediography',
  AWARDS: 'awards'
};

const CelebritiesPage = () => {
  const navigate = useNavigate(); // Initialize navigate hook
  const [searchParams] = useSearchParams(); // Initialize useSearchParams
  const { personId } = useParams(); // Get personId from URL if present
  const location = useLocation(); // For fallback image_url
  const { isDebugMode, t } = useAppContext(); // Get debug mode from context
  const initialSearchQuery = searchParams.get('query') || ''; // Get query from URL
  const inputRef = useRef(null); // Create ref for the input field
  const [celebrityName, setCelebrityName] = useState(initialSearchQuery); // Initialize with URL query
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [celebrityDetails, setCelebrityDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.MEDIOGRAPHY); // Default to Mediography tab
  const [sortConfig, setSortConfig] = useState({ key: 'year', direction: 'desc' }); // Default sort by year descending

  // Search for celebrities when the user types (with debounce)
  useEffect(() => {
    if (celebrityName.length < 2) {
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const results = await apiService.searchCelebrities({ query: celebrityName, debug: isDebugMode }); // Pass object with query and debug
        setSearchResults(results.data || []);
      } catch (error) {
        console.error('Error searching celebrities:', error);
      } finally {
        setIsLoading(false);
      }
    }, 800); // 800ms debounce

    return () => clearTimeout(debounceTimer);
  }, [celebrityName, isDebugMode]);

  const handleInputChange = (e) => {
    setCelebrityName(e.target.value);
    // If viewing celebrity details, navigate back to search results view
    if (personId) {
      navigate('/celebrities');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (celebrityName.length < 2) return;

    setIsLoading(true);
    try {
      const results = await apiService.searchCelebrities({ query: celebrityName, debug: isDebugMode }); // Pass object with query and debug
      setSearchResults(results.data || []);
    } catch (error) {
      console.error('Error searching celebrities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // When a celebrity is clicked, navigate to /celebrities/:personId
  const handleCelebrityClick = (celebrity) => {
    // Pass image_url via navigation state for immediate display while details load
    navigate(`/celebrities/${celebrity.personId}`, { state: { image_url: celebrity.image_url } });
  };

  // Auto-focus the input field when not viewing celebrity details
  useEffect(() => {
    if (!personId && inputRef.current) {
      inputRef.current.focus();
    }
  }, [personId]);

  // Fetch celebrity details if personId is present in the URL
  useEffect(() => {
    if (personId) {
      setIsLoadingDetails(true);
      setCelebrityDetails(null);
      setError(null);
      apiService.getCelebrityDetails(personId)
        .then((details) => {
          setCelebrityDetails(details);
        })
        .catch((error) => {
          console.error('Error fetching celebrity details:', error);
          setError('Failed to load celebrity details. Please try again later.');
        })
        .finally(() => setIsLoadingDetails(false));
    } else {
      setCelebrityDetails(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]);

  const handleBackToSearch = () => {
    // Navigate back to the search page, preserving the search query
    const params = new URLSearchParams();
    if (celebrityName) {
      params.set('query', celebrityName);
    }
    navigate(`/celebrities${params.toString() ? `?${params.toString()}` : ''}`);
  };

  // Function to handle sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort the data based on the current sort configuration
  const getSortedData = (data, type) => {
    if (!data || data.length === 0) return [];
    const sortableData = [...data];
    sortableData.sort((a, b) => {
      // Handle null or undefined values
      if (a[sortConfig.key] === null || a[sortConfig.key] === undefined) return 1;
      if (b[sortConfig.key] === null || b[sortConfig.key] === undefined) return -1;
      // Convert to numbers for year comparison
      if (sortConfig.key === 'year') {
        const yearA = a[sortConfig.key] ? parseInt(a[sortConfig.key], 10) : 0;
        const yearB = b[sortConfig.key] ? parseInt(b[sortConfig.key], 10) : 0;
        if (sortConfig.direction === 'asc') {
          return yearA - yearB;
        } else {
          return yearB - yearA;
        }
      }
      // String comparison for other fields
      if (sortConfig.direction === 'asc') {
        return a[sortConfig.key].localeCompare(b[sortConfig.key]);
      } else {
        return b[sortConfig.key].localeCompare(a[sortConfig.key]);
      }
    });
    return sortableData;
  };

  // Memoize the sorted data to avoid recalculating on every render
  const sortedMediaography = useMemo(() => {
    if (celebrityDetails?.data?.mediography) {
      return getSortedData(celebrityDetails.data.mediography, 'mediography');
    }
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebrityDetails?.data?.mediography, sortConfig, getSortedData]); // Added getSortedData

  const sortedAwards = useMemo(() => {
    if (celebrityDetails?.data?.awards) {
      return getSortedData(celebrityDetails.data.awards, 'awards');
    }
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebrityDetails?.data?.awards, sortConfig, getSortedData]); // Added getSortedData

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 bg-white sticky top-0 z-10 border-b">
        <h2 className="text-2xl font-semibold mb-2">{t('celebrity.searchTitle')}</h2>
        <form onSubmit={handleSubmit} className="flex items-center gap-2 mb-2">
          <input
            type="text"
            id="celebrityName"
            ref={inputRef}
            value={celebrityName}
            onChange={handleInputChange}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('celebrity.placeholder')}
          />
          <button
            type="submit"
            className="bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {t('celebrity.searchButton')}
          </button>
        </form>
        {isLoading && (
          <div className="flex justify-center my-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>
      <div className="p-6 overflow-y-auto flex-1">
        {personId ? (
          isLoadingDetails ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-600">{t('celebrity.loadingDetails')}</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-6">
                <button
                  onClick={handleBackToSearch}
                  className="mr-4 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-md transition-colors"
                >
                  {t('celebrity.backToSearch')}
                </button>
                <h2 className="text-2xl font-bold">{t('celebrity.detailsTitle')}</h2>
              </div>
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p>{error}</p>
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => personId && apiService.getCelebrityDetails(personId).then(setCelebrityDetails).catch(() => setError(t('celebrity.errorDetails')))}
                    className="bg-red-100 hover:bg-red-200 text-red-800 py-1 px-3 rounded-md text-sm transition-colors"
                  >
                    {t('celebrity.tryAgain')}
                  </button>
                </div>
              </div>
            </div>
          ) : celebrityDetails ? (
            <div className="bg-white rounded-lg shadow-md p-6">
              {/* Header with back button and name */}
              <div className="flex items-center mb-6">
                <button
                  onClick={handleBackToSearch}
                  className="mr-4 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-md transition-colors"
                >
                  {t('celebrity.backToSearch')}
                </button>
                <h2 className="text-2xl font-bold">{celebrityDetails.data.fullName}</h2>
              </div>
              {/* Basic Information with Image */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 pb-2 border-b">{t('celebrity.basicInfo')}</h3>
                <div className="flex flex-col md:flex-row">
                  <div className="flex-1">
                    <p className="mb-2"><span className="font-medium">{t('celebrity.fullName')}:</span> {celebrityDetails.data.fullName}</p>
                    <p className="mb-2"><span className="font-medium">{t('celebrity.personId')}:</span> {celebrityDetails.data.personId}</p>
                    {celebrityDetails.data.names && celebrityDetails.data.names.length > 1 && (
                      <div className="mt-2">
                        <p className="font-medium">{t('celebrity.alternativeNames')}</p>
                        <ul className="list-disc list-inside ml-2">
                          {celebrityDetails.data.names
                            .filter(name => !name.isPrimary)
                            .map((name, index) => (
                              <li key={index}>
                                {name.first} {name.middle} {name.last} {name.lang !== 'en' ? `(${name.lang})` : ''}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  {/* Celebrity Image */}
                  <div className="w-48 h-48 mt-4 md:mt-0 flex items-center justify-center bg-gray-50">
                    {celebrityDetails.data.image_url || (location.state && location.state.image_url) ? (
                      <img
                        src={celebrityDetails.data.image_url || location.state?.image_url}
                        alt={celebrityDetails.data.fullName}
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <div className="text-gray-400 flex flex-col items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="mt-2">{t('celebrity.noImage')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Tabs - styled like ProgramDetails */}
              <div className="flex border-b border-gray-300 mt-2 bg-white mb-6">
                <button
                  className={`px-4 py-2 text-sm ml-2 ${activeTab === TABS.MEDIOGRAPHY ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                  onClick={() => setActiveTab(TABS.MEDIOGRAPHY)}
                >
                  {t('celebrity.tabs.mediography')}
                </button>
                {celebrityDetails.data.awards && celebrityDetails.data.awards.length > 0 && (
                  <button
                    className={`px-4 py-2 text-sm ${activeTab === TABS.AWARDS ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                    onClick={() => setActiveTab(TABS.AWARDS)}
                  >
                    {t('celebrity.tabs.awards')}
                  </button>
                )}
              </div>
              {/* Tab Content */}
              {activeTab === TABS.AWARDS && sortedAwards.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => requestSort('award')}
                        >
                          {t('celebrity.columns.award')} {sortConfig.key === 'award' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => requestSort('category')}
                        >
                          {t('celebrity.columns.category')} {sortConfig.key === 'category' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => requestSort('year')}
                        >
                          {t('celebrity.columns.year')} {sortConfig.key === 'year' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => requestSort('won')}
                        >
                          {t('celebrity.columns.result')} {sortConfig.key === 'won' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedAwards.map((award, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 text-left text-sm font-medium text-gray-900">{award.award}</td>
                          <td className="px-6 py-4 text-left text-sm text-gray-500">{award.category}</td>
                          <td className="px-6 py-4 text-left text-sm text-gray-500">{award.year}</td>
                          <td className="px-6 py-4 text-left text-sm text-gray-500">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${award.won ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {award.won ? t('celebrity.results.won') : t('celebrity.results.nominated')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {activeTab === TABS.MEDIOGRAPHY && sortedMediaography.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => requestSort('title')}
                        >
                          {t('celebrity.columns.title')} {sortConfig.key === 'title' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => requestSort('year')}
                        >
                          {t('celebrity.columns.year')} {sortConfig.key === 'year' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => requestSort('type')}
                        >
                          {t('celebrity.columns.type')} {sortConfig.key === 'type' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => requestSort('role')}
                        >
                          {t('celebrity.columns.role')} {sortConfig.key === 'role' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {t('celebrity.columns.character')}
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => requestSort('rootId')}
                        >
                          {t('celebrity.columns.rootId')} {sortConfig.key === 'rootId' && (
                            <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedMediaography.map((media, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 text-left text-sm font-medium text-gray-900">{media.title}</td>
                          <td className="px-6 py-4 text-left text-sm text-gray-500">{media.year || 'N/A'}</td>
                          <td className="px-6 py-4 text-left text-sm text-gray-500">{media.type}</td>
                          <td className="px-6 py-4 text-left text-sm text-gray-500">{media.role}</td>
                          <td className="px-6 py-4 text-left text-sm text-gray-500">{media.characterName || 'N/A'}</td>
                          <td className="px-6 py-4 text-left text-sm text-gray-500">
                            {media.rootId ? (
                              <a
                                href={`/search-results?rootId=${media.rootId}&type=ALL`}
                                className="text-blue-600 underline hover:text-blue-800"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Search by Root ID"
                              >
                                <CopyableId value={media.rootId} />
                              </a>
                            ) : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null
        ) : (
          searchResults.length > 0 ? (
            <div className="mt-2">
              <h3 className="text-lg font-medium mb-3">{t('celebrity.searchResults')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {searchResults.map((celebrity, index) => (
                  <div
                    key={`${celebrity.personId}-${index}`}
                    className="bg-white rounded-lg shadow overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleCelebrityClick(celebrity)}
                  >
                    <div className="h-48 bg-gray-200 flex items-center justify-center">
                      {celebrity.image_url ? (
                        <img
                          src={celebrity.image_url}
                          alt={celebrity.fullName}
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <div className="text-gray-400 flex flex-col items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="mt-2">{t('celebrity.noImage')}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900">{celebrity.fullName}</h4>
                      <p className="text-sm text-gray-500">{t('celebrity.personId')}: {celebrity.personId}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            celebrityName.length >= 2 && !isLoading && (
              <div className="text-center text-gray-500 my-8">
                {t('celebrity.noResults', { query: celebrityName })}
              </div>
            )
          )
        )}
      </div>
    </div>
  );
};

export default CelebritiesPage;
